import { useCallback } from 'react'
import { type TrackingEventMetadata } from '../types'
import { useDomainIntl } from '../utils/intl'
import { getMoneyValue } from './moneyValue'

interface Money {
  currencyCode: string
  nanos: string | number | null
  units: string | number | null
}

/**
 * Custom hook for formatting various values with error handling and tracking.
 *
 * @param trackEvent - Optional callback function for tracking formatting errors
 * @returns Object containing formatting functions
 *
 * @example
 * ```tsx
 * const { formatCurrency } = useFormattedValue((event, metadata) => {
 *   console.log('Formatting error:', event, metadata)
 * })
 *
 * const formatted = formatCurrency({
 *   currencyCode: 'USD',
 *   units: '15',
 *   nanos: '2500'
 * })
 * // Returns: "USD 15.25"
 * ```
 */
export const useFormattedValue = (
  trackEvent?: (event: string, metadata: TrackingEventMetadata) => void
) => {
  const { formatNumber } = useDomainIntl()

  /**
   * Formats a Money object into a currency string using Intl.NumberFormat.
   *
   * @param input - Money object containing currency information
   * @param input.currencyCode - ISO currency code (e.g., 'USD', 'EUR')
   * @param input.units - Whole currency units as string or number
   * @param input.nanos - Fractional currency units in nanoseconds (1e9 nanos = 1 unit)
   * @param options - Additional Intl.NumberFormat options (excluding 'style' and 'currency')
   *
   * @returns Formatted currency string or '—' if formatting fails
   *
   * @example
   * ```tsx
   * // Basic usage
   * formatCurrency({
   *   currencyCode: 'USD',
   *   units: '10',
   *   nanos: '5000'
   * })
   * // Returns: "USD 10.50"
   *
   * // Invalid input returns fallback
   * formatCurrency(null)
   * // Returns: "—"
   * ```
   *
   * @remarks
   * - Units can be string or number but must be convertible to a valid number
   * - Nanos represents fractional currency units in nanoseconds; divide by 1e9 to convert to units
   * - If nanos is missing, defaults to 0
   * - Errors are tracked via the optional trackEvent callback
   * - Returns em-dash ('—') fallback character for any formatting errors
   */
  const formatCurrency = useCallback(
    (
      input?: Partial<Money>,
      options?: Exclude<Parameters<typeof formatNumber>[1], 'style' | 'currency'>
    ): string => {
      try {
        if (!input || typeof input !== 'object') {
          throw new Error('Invalid money object')
        }

        const currencyCode = input.currencyCode
        if (!currencyCode || typeof currencyCode !== 'string' || currencyCode.length !== 3) {
          throw new Error('Invalid or missing currency code')
        }

        const units = Number(input.units)
        if (input.units === '' || isNaN(units)) {
          throw new Error('Invalid units value in money object')
        }

        const nanosFromInput = Number(input.nanos ?? 0)
        if (isNaN(nanosFromInput)) {
          throw new Error('Invalid nanos value in money object')
        }

        const amount = getMoneyValue({ units, nanos: nanosFromInput })
        const isNegative = amount < 0

        const formattedAmount = formatNumber(Math.abs(amount), {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: 'code',
          ...options,
        })

        // Insert negative sign after currency code if negative
        // e.g., "CAD 10.05" -> "CAD -10.05"
        if (isNegative) {
          // This assumes the currency code is prefixed (e.g. "CAD 10.05"). If the locale places the
          // currency code elsewhere, fall back to prefixing the whole string with "-".
          if (!formattedAmount.startsWith(currencyCode)) {
            return `-${formattedAmount}`
          }

          const afterCode = formattedAmount.slice(currencyCode.length)
          const whitespaceMatch = afterCode.match(/^\s+/)
          const separator = whitespaceMatch?.[0] ?? ''
          const rest = afterCode.slice(separator.length)
          return `${currencyCode}${separator}-${rest}`
        }

        return formattedAmount
      } catch (error) {
        if (trackEvent) {
          trackEvent('currency_formatting_error', {
            input: JSON.stringify(input),
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        }

        return '—'
      }
    },
    [formatNumber, trackEvent]
  )

  return {
    formatCurrency,
  }
}
